/**
 * HydroJug PR Box Bundle PDP
 * Variant resolver + pill state + ATC button + bundle-card size morph.
 *
 * Reads a variant matrix embedded as <script type="application/json" data-pr-box-variants>.
 * Resolves a Shopify variant ID from two option selections, updates the hidden
 * <input name="id">, dispatches the theme's variant:change event, and reflects
 * state in the ATC button + bundle cards.
 */

(function () {
  'use strict';

  var STATE_EMPTY = 'empty';
  var STATE_PARTIAL = 'partial';
  var STATE_READY = 'ready';
  var STATE_SUBMITTING = 'submitting';
  var STATE_SUCCESS = 'success';
  var STATE_SOLD_OUT = 'sold_out';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      var fmt = (window.Shopify.money_format || window.Shopify.shop && window.Shopify.shop.money_format) || '${{amount}}';
      try { return window.Shopify.formatMoney(cents, fmt); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function smoothScrollTo(el) {
    if (!el) return;
    el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function findClosest(node, selector) {
    while (node && node !== document) {
      if (node.matches && node.matches(selector)) return node;
      node = node.parentNode;
    }
    return null;
  }

  function PRBox(root) {
    this.root = root;
    this.matrix = [];
    this.state = { sweatshirt: null, tee: null };
    this.bundlePrice = parseInt(root.getAttribute('data-bundle-price'), 10) || 0;
    this.atcLabels = JSON.parse(root.getAttribute('data-atc-labels') || '{}');
    this.optionAxes = ['sweatshirt', 'tee'];

    this.parseMatrix();
    this.cacheDOM();
    if (this.atcBtn) this.bindPills();
    if (this.form) this.bindSubmit();
    this.bindBundleCardPills();
    this.markUnavailableOptions();
    this.applyInitialSelection();
    this.applyAtcState();
    this.initInventoryReveal();
  }

  PRBox.prototype.parseMatrix = function () {
    var script = this.root.querySelector('[data-pr-box-variants]');
    if (!script) return;
    try {
      this.matrix = JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[PR Box] variant matrix parse failed', e);
      this.matrix = [];
    }
  };

  PRBox.prototype.cacheDOM = function () {
    this.form = this.root.querySelector('.hjbp-configure__form') || this.root.querySelector('form');
    this.idInput = this.form && this.form.querySelector('input[name="id"]');
    this.atcBtn = this.root.querySelector('[data-pr-box-atc]');
    this.atcLabel = this.atcBtn && this.atcBtn.querySelector('[data-pr-box-atc-label]');
    this.bundlePriceEl = this.root.querySelector('[data-pr-box-bundle-price]');
    this.liveRegion = this.root.querySelector('[data-pr-box-live]');
    this.panels = this.root.querySelectorAll('[data-pr-box-panel]');
    this.pills = this.root.querySelectorAll('[data-pr-box-pill]');
    this.bundleCards = this.root.querySelectorAll('[data-pr-box-bundle-card]');
    this.bundleCardPills = this.root.querySelectorAll('[data-pr-box-card-pill]');
    this.inventory = this.root.querySelector('[data-pr-box-inventory]');
  };

  PRBox.prototype.bindPills = function () {
    var self = this;
    this.pills.forEach(function (pill) {
      pill.addEventListener('click', function (ev) {
        ev.preventDefault();
        if (pill.hasAttribute('data-unavailable')) return;
        var axis = pill.getAttribute('data-pr-box-axis');
        var value = pill.getAttribute('data-pr-box-value');
        if (!axis || self.optionAxes.indexOf(axis) === -1) return;
        self.selectPill(axis, value, pill);
      });
      pill.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          pill.click();
        }
      });
    });
  };

  PRBox.prototype.selectPill = function (axis, value, clickedPill) {
    if (this.state[axis] === value) return;
    this.state[axis] = value;

    var siblings = this.root.querySelectorAll('[data-pr-box-pill][data-pr-box-axis="' + axis + '"]');
    siblings.forEach(function (p) {
      var isSelected = p === clickedPill;
      p.classList.toggle('is-selected', isSelected);
      p.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    var panel = findClosest(clickedPill, '[data-pr-box-panel]');
    if (panel) panel.classList.remove('is-error');

    this.announce(this.labelFor(axis) + ' size ' + value + ' selected');
    this.markUnavailableOptions();
    this.applyResolved();
  };

  PRBox.prototype.labelFor = function (axis) {
    var panel = this.root.querySelector('[data-pr-box-panel][data-pr-box-axis="' + axis + '"]');
    if (panel) {
      var label = panel.querySelector('[data-pr-box-panel-label]');
      if (label) return label.textContent.trim();
    }
    return axis === 'sweatshirt' ? 'Sweatshirt' : 'Tee shirt';
  };

  PRBox.prototype.resolve = function () {
    if (!this.state.sweatshirt || !this.state.tee) return null;
    for (var i = 0; i < this.matrix.length; i++) {
      var v = this.matrix[i];
      if (v.sweatshirt === this.state.sweatshirt && v.tee === this.state.tee) return v;
    }
    return null;
  };

  PRBox.prototype.applyResolved = function () {
    var v = this.resolve();
    if (v) {
      if (this.idInput) this.idInput.value = v.id;
      if (this.bundlePriceEl) this.bundlePriceEl.textContent = formatMoney(v.price);
      this.bundlePrice = v.price;
      try { history.replaceState({}, '', '?variant=' + v.id); } catch (e) { /* swallow */ }
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: v } }));
    }
    this.applyBundleCardBadges();
    this.applyAtcState();
  };

  PRBox.prototype.applyAtcState = function () {
    if (!this.atcBtn) return;
    var picked = (this.state.sweatshirt ? 1 : 0) + (this.state.tee ? 1 : 0);
    var v = this.resolve();
    var state, label;
    var priceTxt = formatMoney(this.bundlePrice);

    if (picked === 0) {
      state = STATE_EMPTY;
      label = (this.atcLabels.empty || 'Select sizes to add') + ' — ' + priceTxt;
    } else if (picked === 1) {
      state = STATE_PARTIAL;
      label = (this.atcLabels.partial || '1 more size needed') + ' — ' + priceTxt;
    } else if (v && !v.available) {
      state = STATE_SOLD_OUT;
      label = this.atcLabels.sold_out || 'Sold out';
    } else if (v) {
      state = STATE_READY;
      label = (this.atcLabels.ready || 'Add bundle to cart') + ' — ' + priceTxt;
    } else {
      state = STATE_SOLD_OUT;
      label = this.atcLabels.sold_out || 'Combination unavailable';
    }

    this.atcBtn.setAttribute('data-state', state);
    this.atcBtn.disabled = state !== STATE_READY;
    if (this.atcLabel) this.atcLabel.textContent = label;
  };

  PRBox.prototype.markUnavailableOptions = function () {
    var self = this;
    this.optionAxes.forEach(function (axis) {
      var other = axis === 'sweatshirt' ? 'tee' : 'sweatshirt';
      var otherValue = self.state[other];
      var values = self.collectValues(axis);
      values.forEach(function (val) {
        var hasAvailable = self.matrix.some(function (v) {
          if (v[axis] !== val) return false;
          if (otherValue && v[other] !== otherValue) return false;
          return v.available;
        });
        var pills = self.root.querySelectorAll('[data-pr-box-pill][data-pr-box-axis="' + axis + '"][data-pr-box-value="' + cssEscape(val) + '"]');
        pills.forEach(function (p) {
          if (hasAvailable) {
            p.removeAttribute('data-unavailable');
            p.removeAttribute('aria-disabled');
          } else {
            p.setAttribute('data-unavailable', '');
            p.setAttribute('aria-disabled', 'true');
          }
        });
      });
    });
  };

  PRBox.prototype.collectValues = function (axis) {
    var seen = {};
    var values = [];
    this.matrix.forEach(function (v) {
      if (v[axis] && !seen[v[axis]]) {
        seen[v[axis]] = true;
        values.push(v[axis]);
      }
    });
    return values;
  };

  PRBox.prototype.applyInitialSelection = function () {
    var selected = this.root.querySelector('[data-pr-box-initial]');
    if (!selected) return;
    try {
      var data = JSON.parse(selected.textContent);
      if (data && data.sweatshirt && data.tee) {
        this.state.sweatshirt = data.sweatshirt;
        this.state.tee = data.tee;
        var self = this;
        this.pills.forEach(function (p) {
          var axis = p.getAttribute('data-pr-box-axis');
          var value = p.getAttribute('data-pr-box-value');
          var isSel = self.state[axis] === value;
          p.classList.toggle('is-selected', isSel);
          p.setAttribute('aria-checked', isSel ? 'true' : 'false');
        });
        // Sync the hidden id input (and price/URL) to the resolved variant on
        // load, rather than relying on the Liquid-rendered default. Prevents
        // posting a stale/empty id that the cart rejects with "Cannot find variant".
        this.applyResolved();
      }
    } catch (e) { /* swallow */ }
  };

  PRBox.prototype.applyBundleCardBadges = function () {
    var self = this;
    this.bundleCards.forEach(function (card) {
      var axis = card.getAttribute('data-pr-box-bundle-card');
      if (axis !== 'sweatshirt' && axis !== 'tee') return;
      var pill = card.querySelector('[data-pr-box-card-pill]');
      if (!pill) return;
      var value = self.state[axis];
      var defaultLabel = pill.getAttribute('data-default-label') || 'Select a Size';
      var resolvedTemplate = pill.getAttribute('data-resolved-template') || 'Size __VALUE__';
      var label = pill.querySelector('[data-pr-box-card-pill-label]');
      if (value) {
        card.classList.add('is-resolved');
        if (label) label.textContent = resolvedTemplate.replace('__VALUE__', value);
      } else {
        card.classList.remove('is-resolved');
        if (label) label.textContent = defaultLabel;
      }
    });
  };

  PRBox.prototype.bindBundleCardPills = function () {
    var self = this;
    this.bundleCardPills.forEach(function (pill) {
      if (pill.classList.contains('is-fixed')) return;
      pill.addEventListener('click', function (ev) {
        ev.preventDefault();
        var card = findClosest(pill, '[data-pr-box-bundle-card]');
        if (!card) return;
        var axis = card.getAttribute('data-pr-box-bundle-card');
        var targetPanel = self.root.querySelector('[data-pr-box-panel][data-pr-box-axis="' + axis + '"]');
        smoothScrollTo(targetPanel);
        if (targetPanel) {
          var firstPill = targetPanel.querySelector('[data-pr-box-pill]:not([data-unavailable])');
          if (firstPill) setTimeout(function () { firstPill.focus(); }, reducedMotion ? 0 : 350);
        }
      });
    });
  };

  PRBox.prototype.bindSubmit = function () {
    var self = this;
    this.form.addEventListener('submit', function (ev) {
      var v = self.resolve();
      if (!v || !v.available) {
        ev.preventDefault();
        self.flagMissingPanels();
        return;
      }
      if (self.atcBtn) {
        self.atcBtn.setAttribute('data-state', STATE_SUBMITTING);
        if (self.atcLabel) self.atcLabel.textContent = self.atcLabels.submitting || 'Adding…';
      }
    });

    document.addEventListener('cart:item-added', function () {
      if (!self.atcBtn) return;
      self.atcBtn.setAttribute('data-state', STATE_SUCCESS);
      if (self.atcLabel) self.atcLabel.textContent = self.atcLabels.success || '✓ Added to cart';
      setTimeout(function () { self.applyAtcState(); }, 1500);
    });
  };

  PRBox.prototype.flagMissingPanels = function () {
    var self = this;
    var firstMissing = null;
    this.optionAxes.forEach(function (axis) {
      if (self.state[axis]) return;
      var panel = self.root.querySelector('[data-pr-box-panel][data-pr-box-axis="' + axis + '"]');
      if (!panel) return;
      panel.classList.remove('is-error');
      void panel.offsetWidth;
      panel.classList.add('is-error');
      if (!firstMissing) firstMissing = panel;
    });
    if (firstMissing) {
      smoothScrollTo(firstMissing);
      this.announce('Pick a size to continue.');
    }
  };

  PRBox.prototype.announce = function (msg) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    var lr = this.liveRegion;
    setTimeout(function () { lr.textContent = msg; }, 20);
  };

  PRBox.prototype.initInventoryReveal = function () {
    if (!this.inventory || reducedMotion) {
      if (this.inventory) {
        var fill = this.inventory.querySelector('[data-pr-box-inventory-fill]');
        var pct = this.inventory.getAttribute('data-pr-box-inventory-pct') || '0';
        if (fill) fill.style.width = pct + '%';
      }
      return;
    }
    var inv = this.inventory;
    var fill = inv.querySelector('[data-pr-box-inventory-fill]');
    var pct = inv.getAttribute('data-pr-box-inventory-pct') || '0';
    if (!fill || !('IntersectionObserver' in window)) {
      if (fill) fill.style.width = pct + '%';
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          fill.style.width = pct + '%';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    observer.observe(inv);
  };

  function cssEscape(value) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(value);
    return String(value).replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, '\\$&');
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-pr-box-pdp]');
    roots.forEach(function (root) {
      if (root.__prbox) return;
      root.__prbox = new PRBox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev && ev.target) init(ev.target);
  });
})();
